import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/Home.css";

export default function Home() {
  const navigate = useNavigate();

  /* =========================
     STATE (DATA)
  ========================= */
  const [books, setBooks] = useState([]);
  const [search, setSearch] = useState("");

  const [showPopup, setShowPopup] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  /* 🔹 genre added */
  const [form, setForm] = useState({
    bookName: "",
    author: "",
    year: "",
    email: "",
    genre: ""
  });

  /* =========================
     UI STATE
  ========================= */
  const [cardView, setCardView] = useState(false);
  const [lightMode, setLightMode] = useState(false);

  /* =========================
     FETCH BOOKS
  ========================= */
  useEffect(() => {
    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    const res = await fetch(
      "https://book-inventory-backend-ze7h.onrender.com/books"
    );
    const data = await res.json();
    setBooks(data);
  };

  /* =========================
     ADD / UPDATE
  ========================= */
  const handleSubmit = async () => {
    if (!form.bookName || !form.author) {
      alert("Book name and author are required");
      return;
    }

    if (isEditing) {
      await fetch(
        `https://book-inventory-backend-ze7h.onrender.com/books/${editId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form)
        }
      );
    } else {
      await fetch(
        "https://book-inventory-backend-ze7h.onrender.com/books",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form)
        }
      );
    }

    await fetchBooks();
    closePopup();
  };

  /* =========================
     DELETE
  ========================= */
  const deleteBook = async (id) => {
    if (!window.confirm("Delete this book?")) return;

    await fetch(
      `https://book-inventory-backend-ze7h.onrender.com/books/${id}`,
      { method: "DELETE" }
    );

    setBooks(books.filter((b) => b.id !== id));
  };

  /* =========================
     HELPERS
  ========================= */
  const openAddPopup = () => {
    setForm({
      bookName: "",
      author: "",
      year: "",
      email: "",
      genre: ""
    });
    setIsEditing(false);
    setEditId(null);
    setShowPopup(true);
  };

  const openEditPopup = (b) => {
    setForm({
      bookName: b.bookName || "",
      author: b.author || "",
      year: b.year || "",
      email: b.email || "",
      genre: b.genre || ""
    });
    setIsEditing(true);
    setEditId(b.id);
    setShowPopup(true);
  };

  const closePopup = () => {
    setShowPopup(false);
    setIsEditing(false);
    setEditId(null);
  };

  /* =========================
     SEARCH (NAME + AUTHOR + EMAIL)
  ========================= */
  const filteredBooks = books.filter((b) => {
    const term = search.toLowerCase();
    return (
      (b.bookName || "").toLowerCase().includes(term) ||
      (b.author || "").toLowerCase().includes(term) ||
      (b.email || "").toLowerCase().includes(term)
    );
  });

  return (
    <>
      {/* MODE SWITCH */}
      <div className="theme-toggle-btn">
        <button
          onClick={() => {
            setLightMode(!lightMode);
            document.body.classList.toggle("theme-light");
          }}
        >
          {lightMode ? "🌙" : "☀️"}
        </button>
      </div>

      {/* BACKGROUND */}
      <div className="bg-sky">
        <div className="aurora"></div>
        <div className="moon"></div>
        <div className="sun"></div>

        <div className="meteor m1"></div>
        <div className="meteor m2"></div>
        <div className="meteor m3"></div>
        <div className="meteor m4"></div>

        <div className="cloud c1"></div>
        <div className="cloud c2"></div>
        <div className="cloud c3"></div>
      </div>

      {/* UI */}
      <div className="ui">
        <header className="header">
          <h1 className="logo">Book Inventory</h1>
          <p className="subtitle">
            “A room without books is like a body without a soul.”
          </p>
        </header>

        {/* SEARCH + ADD */}
        <div className="top-bar">
          <input
            className="search"
            placeholder="Search ...."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button className="add-btn" onClick={openAddPopup}>
            + Add Book
          </button>
        </div>

        {/* TABLE ↔ CARD SWITCH */}
        <div className="view-switch">
          <span>Table</span>
          <label className="switch">
            <input
              type="checkbox"
              checked={cardView}
              onChange={() => setCardView(!cardView)}
            />
            <span className="slider"></span>
          </label>
          <span>Cards</span>
        </div>

        {/* TABLE VIEW */}
        {!cardView && (
          <div className="table-card">
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Sr</th>
                    <th>Book</th>
                    <th>Author</th>
                    <th>Year</th>
                    <th>Email</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBooks.map((b, i) => (
                    <tr key={b.id}>
                      <td>{i + 1}</td>
                      <td
                        className="book-link"
                        onClick={() => navigate(`/book/${b.id}`)}
                      >
                        {b.bookName}
                      </td>
                      <td>{b.author}</td>
                      <td>{b.year}</td>
                      <td>{b.email}</td>
                      <td>
                        <button
                          className="icon-btn"
                          onClick={() => openEditPopup(b)}
                        >
                          ✏️
                        </button>
                        <button
                          className="icon-btn danger"
                          onClick={() => deleteBook(b.id)}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* CARD VIEW */}
        {cardView && (
          <div className="card-view active">
            {filteredBooks.map((b) => (
              <div className="book-card" key={b.id}>
                <div className="title">{b.bookName}</div>
                <div className="author">{b.author}</div>
                <div className="meta">
                  <span>{b.year}</span>
                  <span>{b.email}</span>
                </div>
                <div className="actions">
                  <button
                    className="icon-btn"
                    onClick={() => openEditPopup(b)}
                  >
                    ✏️
                  </button>
                  <button
                    className="icon-btn danger"
                    onClick={() => deleteBook(b.id)}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* POPUP */}
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-card">
            <span className="popup-close" onClick={closePopup}>✕</span>

            <h2>{isEditing ? "Edit Book Details" : "Add New Book"}</h2>

            <input
              placeholder="Book Name"
              value={form.bookName}
              onChange={(e) =>
                setForm({ ...form, bookName: e.target.value })
              }
            />
            <input
              placeholder="Author"
              value={form.author}
              onChange={(e) =>
                setForm({ ...form, author: e.target.value })
              }
            />
            <input
              placeholder="Year"
              value={form.year}
              onChange={(e) =>
                setForm({ ...form, year: e.target.value })
              }
            />
            <input
              placeholder="Email"
              value={form.email}
              onChange={(e) =>
                setForm({ ...form, email: e.target.value })
              }
            />

            {/* 🔹 GENRE INPUT */}
            <input
              placeholder="Genre"
              value={form.genre}
              onChange={(e) =>
                setForm({ ...form, genre: e.target.value })
              }
            />

            <div className="popup-actions">
              <button className="secondary" onClick={closePopup}>
                Cancel
              </button>
              <button className="primary" onClick={handleSubmit}>
                {isEditing ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
